import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { gql, useLazyQuery } from '@apollo/client';
import { ResourceKey } from 'i18next';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import {
  Contact,
  Maybe,
  Query,
  QueryScheduleArgs,
  QueryTaskArgs,
  Schedule,
  Task,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { AppointmentStatusSelector } from 'src/components/domain/appointments/appointment-status-selector';
import {
  CompletedEventDate,
  TableLayoutDate,
} from 'src/components/general/display/table-layout-date';
import { ListLoadingItem } from 'src/components/general/feedback/list-loading-item';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { AppointmentPriorityPicker } from 'src/components/pages/appointments/components/appointment-priority-picker';
import { AppointmentOptionsBottomDrawer } from 'src/components/pages/appointments/components/appointments-options-bottom-drawer';
import {
  CardBaseContent,
  CardListScrollOverlayIndicator,
  DashboardCard,
  ellipsisTextStyle,
  emptyValuePlaceholder,
} from 'src/components/pages/dashboard/components/shared';
import {
  ChangeTaskStatusDrawer,
  TaskOptionsBottomDrawer,
} from 'src/components/pages/tasks/components/task-options-bottom-drawer';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useUrls } from 'src/hooks/use-urls';
import { useVisible } from 'src/hooks/use-visible';
import {
  baseListTitleRegularStyles,
  baseListMetaStyles,
  baseListStormStyles,
  baseListContainerStyles,
  baseListItemStyles,
  baseEllipsisStyles,
  DashboardAssigneeIcon,
  StyledContactLabel,
} from './base-list';

const REFETCH_TASK_QUERY = gql`
  query GetTask($id: Int!) {
    task(id: $id) {
      id
      title
      status
      description
      dueAt
    }
  }
`;

const REFETCH_APPOINTMENT_QUERY = gql`
  query GetAppointment($id: Int!) {
    schedule(id: $id) {
      id
      status
      priority
    }
  }
`;

const StyledCard = styled(DashboardCard)`
  width: 100%;
`;

const ItemsList = styled.div`
  ${baseListContainerStyles}
  gap: 10px;
`;

const ListItemWrapper = styled.div`
  ${baseListItemStyles}

  &:first-child {
    padding-top: 15px;
  }

  &:last-child {
    padding-bottom: 15px;
  }
`;

const ItemContainer = styled(FlexColumn)`
  ${baseListItemStyles}
  height: 65px;
  gap: 3px;
  padding-bottom: 10px;
`;

const itemTitleStyles = css`
  ${baseListTitleRegularStyles}
  ${ellipsisTextStyle}
  text-decoration: none;
  color: inherit;
`;

const completedTitleStyles = css`
  &,
  &:hover {
    text-decoration: line-through;
  }
`;

const ItemTitleLink = styled(Link)<{ $isCompleted: boolean }>`
  ${itemTitleStyles};

  ${({ $isCompleted }) => $isCompleted && completedTitleStyles};
`;

const ItemTitle = styled.div<{ $isCompleted: boolean }>`
  ${itemTitleStyles};

  ${({ $isCompleted }) => $isCompleted && completedTitleStyles};
`;

const ItemMetaRow = styled(FlexRow)`
  align-items: center;
  gap: 8px;
`;

const DateContainer = styled(FlexRow)`
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
`;

const DateLabel = styled.span`
  ${baseListStormStyles}
`;

const StyledTableLayoutDateWrapper = styled.div`
  && span {
    align-items: center !important;
    display: flex !important;
    flex-direction: row !important;
    font-size: 12px;
    gap: 3px !important;
    margin: 0 !important;
  }

  && .ant-tag {
    height: 16px !important;
    font-size: 11px;
    font-weight: 500;
    line-height: 14px;
    letter-spacing: -0.22px;
  }

  && .time {
    ${baseListMetaStyles}
  }

  && .separator {
    display: none !important;
  }
`;

const DotSeparatorIcon = styled(Icon)`
  align-items: center;
  display: flex;
  height: 100%;

  svg {
    width: 3px;
    height: 3px;
  }
`;

const ItemAssigned = styled(FlexRow)`
  align-items: center;
  gap: 4px;
  ${baseListStormStyles}
`;

const AssigneeName = styled.span`
  ${baseListMetaStyles}
  ${baseEllipsisStyles}
`;

const ContactLabelContainer = styled.div`
  overflow: hidden;
  width: 100%;

  ${StyledContactLabel} {
    &,
    .contact-label-text-column {
      &,
      a {
        width: 100%;
      }
    }
  }
`;

const CardContentContainer = styled(CardBaseContent)`
  height: 311px;
`;

const EmptyCardContentContainer = styled(CardBaseContent)`
  height: 357px;
`;

type TaskOrAppointmentListItemProps = {
  item: TaskOrAppointmentItemData;
  testId: string;
  dateLabel: string;
  index: number;
  onAppointmentUpdate: (appointmentId: number) => void;
  onTaskUpdate: (taskId: number) => void;
};

const TaskOrAppointmentListItem = ({
  item,
  testId,
  dateLabel,
  index,
  onAppointmentUpdate,
  onTaskUpdate,
}: TaskOrAppointmentListItemProps) => {
  const { getTaskModalUrl, getScheduleModalUrl } = useUrls();
  const optionsBottomDrawerVisibilityHandler = useVisible();
  const mobileStatusBottomDrawerVisibilityHandler = useVisible();
  const priorityBottomDrawerVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const { t } = useTranslation(['common']);

  const getModalUrl = useCallback(
    (item: TaskOrAppointmentItemData) =>
      item.type === 'appointment'
        ? getScheduleModalUrl({ scheduleId: item.id })
        : getTaskModalUrl({ taskId: item.id }),
    [getScheduleModalUrl, getTaskModalUrl],
  );

  const isCompleted = useMemo(
    () => item.rawNode.status === 'COMPLETED',
    [item.rawNode.status],
  );
  return (
    <>
      <ItemContainer
        key={item.id}
        data-lgg-id={`${testId}-item-${item.id}`}
        onClick={!breakpointUpMd ? optionsBottomDrawerVisibilityHandler.show : undefined}
      >
        {breakpointUpMd ? (
          <ItemTitleLink
            $isCompleted={isCompleted}
            to={breakpointUpMd ? getModalUrl(item) : undefined}
            data-lgg-id={`${testId}-item-${item.id}-title`}
          >
            {item.title}
          </ItemTitleLink>
        ) : (
          <ItemTitle
            data-lgg-id={`${testId}-item-${item.id}-title`}
            $isCompleted={isCompleted}
          >
            {item.title}
          </ItemTitle>
        )}
        <ItemMetaRow>
          <DateContainer>
            {isCompleted ? (
              <CompletedEventDate
                testId="event-completed-date"
                date={
                  item.type === 'appointment'
                    ? item.rawNode.updatedAt
                    : item.rawNode.completedAt
                }
                label={`${
                  item.type === 'appointment'
                    ? t('common:happened')
                    : t('common:completed')
                }:`}
              />
            ) : (
              <>
                {item.priority}
                <DateLabel>{dateLabel}</DateLabel>
                <StyledTableLayoutDateWrapper>
                  <TableLayoutDate date={item.date} />
                </StyledTableLayoutDateWrapper>
              </>
            )}
          </DateContainer>
          {item.contact && (
            <>
              <DotSeparatorIcon type="circle" />
              <ContactLabelContainer>
                <StyledContactLabel
                  contact={item.contact}
                  testId={`${testId}-contact-${item.contact.id}`}
                  marginSize={5}
                  popoverPlacement={index === 0 ? 'bottomLeft' : 'topLeft'}
                />
              </ContactLabelContainer>
            </>
          )}
        </ItemMetaRow>
        <ItemAssigned>
          <DashboardAssigneeIcon type="assignedto" />
          <AssigneeName
            data-lgg-id={`${testId}-item-${item.id}-assignee-${
              item.assignedTo?.id ?? 'unassigned'
            }`}
          >
            {item.assignedTo?.fullName ?? emptyValuePlaceholder}
          </AssigneeName>
        </ItemAssigned>
      </ItemContainer>
      {!breakpointUpMd ? (
        <>
          {item.type === 'appointment' ? (
            <>
              <AppointmentOptionsBottomDrawer
                onClose={optionsBottomDrawerVisibilityHandler.close}
                selectedAppointment={item.rawNode}
                visible={optionsBottomDrawerVisibilityHandler.visible}
                mobileStatusOptionsVisibilityHandler={
                  mobileStatusBottomDrawerVisibilityHandler
                }
                mobilePriorityOptionsVisibilityHandler={
                  priorityBottomDrawerVisibilityHandler
                }
              />
              <AppointmentPriorityPicker
                appointmentId={item.id}
                priority={item.rawNode.priority}
                mobileVisibilityHandler={priorityBottomDrawerVisibilityHandler}
                onPriorityUpdated={async () => {
                  await onAppointmentUpdate(item.id);
                }}
                isMobileTargetAppointment
                showMobileTrigger={false}
              />
              <AppointmentStatusSelector
                appointment={item.rawNode}
                visibilityHandler={mobileStatusBottomDrawerVisibilityHandler}
                onCompleted={async () => {
                  await onAppointmentUpdate(item.id);
                }}
                showMobileTrigger={false}
              />
            </>
          ) : (
            <>
              <TaskOptionsBottomDrawer
                mobileStatusOptionsVisibilityHandler={
                  mobileStatusBottomDrawerVisibilityHandler
                }
                onClose={optionsBottomDrawerVisibilityHandler.close}
                selectedTask={item.rawNode}
                visible={optionsBottomDrawerVisibilityHandler.visible}
              />
              <ChangeTaskStatusDrawer
                visibilityHandler={mobileStatusBottomDrawerVisibilityHandler}
                onCompleted={async () => {
                  await onTaskUpdate(item.id);
                }}
                task={item.rawNode}
              />
            </>
          )}
        </>
      ) : null}
    </>
  );
};

export type TaskOrAppointmentItemData = {
  id: number;
  title: string;
  date: string;
  contact?: Maybe<Contact>;
  assignedTo?: {
    id: string | number;
    fullName: string;
  };
  priority?: ReactNode;
  rawNode: Task | Schedule;
} & (
  | {
      type: 'task';
      rawNode: Task;
    }
  | { type: 'appointment'; rawNode: Schedule }
);

export type TaskOrAppointmentListProps = {
  title: string;
  titleExtraContent?: React.ReactNode;
  items: TaskOrAppointmentItemData[];
  loading: boolean;
  isEmpty: boolean;
  emptyStateIcon: string;
  emptyStateMessage: string;
  dateLabel: string;
  linkTo: string;
  linkText: string;
  testId: string;
  cardInfoI18nKey: ResourceKey;
  handleLoadBottom: VoidFunction;
  firstItemIndex: number;
  loadingMoreBottom: boolean;
};

export const TaskOrAppointmentList = ({
  title,
  titleExtraContent,
  items,
  loading,
  isEmpty,
  emptyStateIcon,
  emptyStateMessage,
  dateLabel,
  linkTo,
  linkText,
  testId,
  cardInfoI18nKey,
  handleLoadBottom,
  firstItemIndex,
  loadingMoreBottom,
}: TaskOrAppointmentListProps) => {
  const virtuosoListRef = useRef<VirtuosoHandle>(null);
  const [refetchTask] = useLazyQuery<Pick<Query, 'task'>, QueryTaskArgs>(
    REFETCH_TASK_QUERY,
  );
  const [refetchAppointment] = useLazyQuery<Pick<Query, 'schedule'>, QueryScheduleArgs>(
    REFETCH_APPOINTMENT_QUERY,
  );

  const List = React.forwardRef<HTMLDivElement>((props, ref) => (
    <ItemsList ref={ref} {...props} />
  ));

  return (
    <StyledCard loading={loading} data-lgg-id={testId}>
      <DashboardCard.Header
        testId={`${testId}-header`}
        title={title}
        titleExtraContent={titleExtraContent}
        cardTextTitle={title}
        cardInfoI18nKey={cardInfoI18nKey}
      />
      <DashboardCard.Content>
        {isEmpty ? (
          <EmptyCardContentContainer $isEmpty>
            <DashboardCard.EmptyState
              icon={emptyStateIcon}
              title={emptyStateMessage}
              testId={`${testId}-empty`}
            />
          </EmptyCardContentContainer>
        ) : (
          <CardContentContainer>
            <CardListScrollOverlayIndicator $showBottomShadow>
              <Virtuoso
                ref={virtuosoListRef}
                data-lgg-id={`${testId}-item-list`}
                data={items}
                endReached={handleLoadBottom}
                firstItemIndex={firstItemIndex}
                components={{
                  Footer: () => <ListLoadingItem visible={loadingMoreBottom} />,
                  Item: (props) => <ListItemWrapper {...props} />,
                  List,
                }}
                itemContent={(index, item) => {
                  return (
                    <TaskOrAppointmentListItem
                      item={item}
                      testId={testId}
                      key={`${item.type}-${item.id}`}
                      dateLabel={dateLabel}
                      index={index}
                      onAppointmentUpdate={async (id) => {
                        await refetchAppointment({
                          variables: {
                            id,
                          },
                        });
                      }}
                      onTaskUpdate={async (id) => {
                        await refetchTask({
                          variables: {
                            id,
                          },
                        });
                      }}
                    />
                  );
                }}
              />
            </CardListScrollOverlayIndicator>
          </CardContentContainer>
        )}
      </DashboardCard.Content>
      {!isEmpty ? (
        <DashboardCard.Footer
          testId={`${testId}-footer`}
          linkTo={linkTo}
          linkText={linkText}
        />
      ) : null}
    </StyledCard>
  );
};
